import { TimecardTemplateDto } from 'types/timecards';
import { foldForSearch } from 'utils/foldForSearch';

export type TemplateGroup = {
    key: string;
    matterTypeCode: string;
    langCode: string;
    /** Resolved labels: the header prints them and the order compares them, since users read labels, not codes. */
    matterType: string;
    language: string;
    isOwnType: boolean;
    isCaseLanguage: boolean;
    templates: TimecardTemplateDto[];
};

/** The two "Also show" chips, each turning one axis on and off. */
export type TemplateExpansion = { otherLanguages: boolean; otherTypes: boolean };

type Labels = { matterType: (code: string) => string; language: (code: string) => string };

/**
 * One group per (case type, language): the case's own type first, then the other types by label; within
 * a type the case language first, then the other languages by label.
 *
 * `ownTemplates` is the default for-matter answer, which holds the case's own type only — so it is what
 * tells the own type apart, and a case with no type, or no template for its type, has no own group.
 * `allTemplates` is the answer with every type, absent until it has loaded.
 */
export function groupTemplates(
    ownTemplates: TimecardTemplateDto[],
    allTemplates: TimecardTemplateDto[] | undefined,
    caseLanguageCode: string | null | undefined,
    labels: Labels
): TemplateGroup[] {
    const ownTypeCode = ownTemplates[0]?.matterTypeCode;
    const byKey = new Map<string, TemplateGroup>();
    (allTemplates ?? ownTemplates).forEach((template) => {
        const key = `${template.matterTypeCode}|${template.langCode}`;
        const group = byKey.get(key);
        if (group) {
            group.templates.push(template);
        } else {
            byKey.set(key, {
                key,
                matterTypeCode: template.matterTypeCode,
                langCode: template.langCode,
                matterType: labels.matterType(template.matterTypeCode),
                language: labels.language(template.langCode),
                isOwnType: template.matterTypeCode === ownTypeCode,
                isCaseLanguage: template.langCode === caseLanguageCode,
                templates: [template],
            });
        }
    });

    return [...byKey.values()].sort(
        (a, b) =>
            Number(b.isOwnType) - Number(a.isOwnType) ||
            a.matterType.localeCompare(b.matterType) ||
            a.matterTypeCode.localeCompare(b.matterTypeCode) ||
            Number(b.isCaseLanguage) - Number(a.isCaseLanguage) ||
            a.language.localeCompare(b.language)
    );
}

/**
 * The groups the popup lists under `expansion`. When the own type has nothing in the case language its
 * other languages list at once, so the popup never opens on a chip alone. Other types never list unasked;
 * once asked, the same rule applies to them as a whole, so templates they hold only outside the case
 * language (or for a case with no language) are not left behind a chip that never shows.
 */
export function listedGroups(groups: TemplateGroup[], expansion: TemplateExpansion): TemplateGroup[] {
    const allLanguages = (isOwnType: boolean) =>
        expansion.otherLanguages || !groups.some((g) => g.isOwnType === isOwnType && g.isCaseLanguage);
    const ownOtherLanguages = allLanguages(true);
    const otherTypesOtherLanguages = allLanguages(false);
    return groups.filter((group) =>
        group.isOwnType
            ? group.isCaseLanguage || ownOtherLanguages
            : expansion.otherTypes && (group.isCaseLanguage || otherTypesOtherLanguages)
    );
}

/** Whether switching on `more` would list a group that `expansion` does not, i.e. whether its chip leads anywhere. */
export function revealsMore(
    groups: TemplateGroup[],
    expansion: TemplateExpansion,
    more: Partial<TemplateExpansion>
): boolean {
    return listedGroups(groups, { ...expansion, ...more }).length > listedGroups(groups, expansion).length;
}

/** Narrows the listed groups to the templates whose text matches `search`; a group left empty is dropped. */
export function filterGroups(groups: TemplateGroup[], search: string): TemplateGroup[] {
    const query = foldForSearch(search.trim());
    if (query === '') return groups;
    return groups
        .map((group) => ({
            ...group,
            templates: group.templates.filter((template) => foldForSearch(template.templateText).includes(query)),
        }))
        .filter((group) => group.templates.length > 0);
}

/** How many templates matching `search` the `axis` adds on top of the rest of `expansion`: the number its chip shows, pressed or not. */
export function addedCount(
    groups: TemplateGroup[],
    expansion: TemplateExpansion,
    axis: keyof TemplateExpansion,
    search: string
): number {
    const count = (on: boolean) =>
        filterGroups(listedGroups(groups, { ...expansion, [axis]: on }), search).reduce(
            (n, group) => n + group.templates.length,
            0
        );
    return count(true) - count(false);
}
